/**
 * Time helpers for callable payloads and the desk's first-contact promise.
 */
/**
 * A Firestore Timestamp crosses the callable boundary as a plain object
 * (`{ _seconds, _nanoseconds }` from the Admin SDK's JSON encoding). Accept
 * that, the public `{ seconds }` shape, epoch milliseconds, and ISO strings,
 * so the UI is not coupled to one serialiser. Returns null when unknown.
 */
export function toMillis(value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const t = Date.parse(value);
        return Number.isNaN(t) ? null : t;
    }
    if (typeof value === 'object') {
        const v = value;
        const seconds = typeof v._seconds === 'number' ? v._seconds : typeof v.seconds === 'number' ? v.seconds : null;
        if (seconds !== null) {
            const nanos = typeof v._nanoseconds === 'number' ? v._nanoseconds : typeof v.nanoseconds === 'number' ? v.nanoseconds : 0;
            return seconds * 1000 + Math.floor(nanos / 1e6);
        }
        if (typeof v.value === 'string')
            return toMillis(v.value);
    }
    return null;
}
const DATE_FMT = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME_FMT = new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
});
export function formatDate(value) {
    const ms = toMillis(value);
    return ms === null ? '-' : DATE_FMT.format(new Date(ms));
}
export function formatDateTime(value) {
    const ms = toMillis(value);
    return ms === null ? '-' : DATE_TIME_FMT.format(new Date(ms));
}
/** Compact age: "12m", "5h", "3d". */
export function formatAge(fromMs, nowMs) {
    if (fromMs === null)
        return '-';
    const mins = Math.max(0, Math.floor((nowMs - fromMs) / 60000));
    if (mins < 60)
        return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 48)
        return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
}
/** Business day window, in the viewer's local time. No public-holiday calendar. */
export const BUSINESS_DAY_START_HOUR = 9;
export const BUSINESS_DAY_END_HOUR = 17;
/**
 * Milliseconds of business time (Mon to Fri, 09:00 to 17:00 local) between
 * two instants. Walks day by day, so it is exact across weekends and DST.
 */
export function businessMsBetween(startMs, endMs) {
    if (!(endMs > startMs))
        return 0;
    let total = 0;
    const day = new Date(startMs);
    day.setHours(0, 0, 0, 0);
    // Bounded: a referral older than ~2 years is simply "very overdue".
    for (let i = 0; i < 800 && day.getTime() < endMs; i++) {
        const dow = day.getDay();
        if (dow !== 0 && dow !== 6) {
            const open = new Date(day);
            open.setHours(BUSINESS_DAY_START_HOUR, 0, 0, 0);
            const close = new Date(day);
            close.setHours(BUSINESS_DAY_END_HOUR, 0, 0, 0);
            const from = Math.max(open.getTime(), startMs);
            const to = Math.min(close.getTime(), endMs);
            if (to > from)
                total += to - from;
        }
        day.setDate(day.getDate() + 1);
    }
    return total;
}
/**
 * A referral still at `submitted` after more than the partner's first-contact
 * promise (in business hours) is overdue. A partner with no promise set
 * (`null`) has no overdue rule at all: nothing is ever overdue.
 */
export function isFirstContactOverdue(stage, stageChangedMs, nowMs, promiseHours) {
    if (promiseHours === null || stage !== 'submitted' || stageChangedMs === null)
        return false;
    return businessMsBetween(stageChangedMs, nowMs) > promiseHours * 3600000;
}
/**
 * The partner's first-contact promise in business hours, from the partner
 * profile (`firstContactBusinessHours`, flat or under `publicProfile`).
 * Sources are tried in order and the first that carries the field decides,
 * so a fresher source (partnerGetMe) overrides the page-load profile. Missing,
 * null or not a sensible positive number means no promise: null.
 */
export function readFirstContactHours(...sources) {
    for (const s of sources) {
        if (!s || typeof s !== 'object')
            continue;
        const o = s;
        const v = o.firstContactBusinessHours !== undefined ? o.firstContactBusinessHours : o.publicProfile?.firstContactBusinessHours;
        if (v === undefined)
            continue;
        return typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 1000 ? v : null;
    }
    return null;
}
/** "1 business hour", "4 business hours", "2.5 business hours". */
export function formatBusinessHours(hours) {
    return `${hours} business hour${hours === 1 ? '' : 's'}`;
}
